import React, { createContext, useContext, useEffect, useState } from 'react';
import { CartItemState } from '@/types/club-sales-order';

const CART_KEY = 'club-sales-cart';

interface CartContextValue {
  items: CartItemState[];
  addItem: (item: CartItemState) => void;
  updateItem: (index: number, item: CartItemState) => void;
  removeItem: (index: number) => void;
  clearCart: () => void;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<CartItemState[]>(() => {
    try {
      const stored = localStorage.getItem(CART_KEY);
      return stored ? (JSON.parse(stored) as CartItemState[]) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(CART_KEY, JSON.stringify(items));
    } catch {
      // localStorage quota or disabled — fail silently; cart still works in-memory.
    }
  }, [items]);

  const addItem = (item: CartItemState) =>
    setItems((prev) => [...prev, { ...item, cartItemId: item.cartItemId ?? crypto.randomUUID() }]);
  const updateItem = (index: number, item: CartItemState) =>
    setItems((prev) => prev.map((it, i) => (i === index ? item : it)));
  const removeItem = (index: number) =>
    setItems((prev) => prev.filter((_, i) => i !== index));
  const clearCart = () => setItems([]);

  return (
    <CartContext.Provider value={{ items, addItem, updateItem, removeItem, clearCart }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within a CartProvider');
  return ctx;
}
